import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  NotificationListResponse,
  NotificationTab,
  NotificationUnreadCountResponse,
} from '@fluenceur/types';
import { NotificationTab as NotificationTabValue } from '@fluenceur/types';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

const EMPTY_NOTIFICATIONS_RESPONSE: NotificationListResponse = {
  notifications: [],
  paging: {
    hasMore: false,
    nextCursor: null,
  },
};

const EMPTY_UNREAD_COUNT_RESPONSE: NotificationUnreadCountResponse = {
  total: 0,
  agents: 0,
};

export const notificationQueryKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationQueryKeys.all, 'list'] as const,
  list: (tab: NotificationTab, limit: number, cursor?: string | null) =>
    [...notificationQueryKeys.lists(), { cursor: cursor ?? null, limit, tab }] as const,
  infiniteList: (tab: NotificationTab, limit: number) =>
    [...notificationQueryKeys.lists(), 'infinite', { limit, tab }] as const,
  unreadCount: () => [...notificationQueryKeys.all, 'unread-count'] as const,
};

type UseNotificationsQueryOptions = {
  tab?: NotificationTab;
  limit?: number;
  cursor?: string | null;
  enabled?: boolean;
};

async function fetchNotifications({
  cursor,
  tab,
  limit,
}: Required<
  Pick<UseNotificationsQueryOptions, 'cursor' | 'tab' | 'limit'>
>): Promise<NotificationListResponse> {
  const response = await api.get<ApiResponse<NotificationListResponse>>(
    '/dashboard/notifications',
    {
      params: {
        cursor,
        tab,
        limit,
      },
    }
  );

  return response.data.data ?? EMPTY_NOTIFICATIONS_RESPONSE;
}

async function fetchNotificationUnreadCount(): Promise<NotificationUnreadCountResponse> {
  const response = await api.get<ApiResponse<NotificationUnreadCountResponse>>(
    '/dashboard/notifications/unread-count'
  );

  return response.data.data ?? EMPTY_UNREAD_COUNT_RESPONSE;
}

export function useNotificationsQuery({
  tab = NotificationTabValue.ALL,
  limit = 20,
  cursor = null,
  enabled = true,
}: UseNotificationsQueryOptions = {}) {
  return useQuery<NotificationListResponse, Error>({
    queryKey: notificationQueryKeys.list(tab, limit, cursor),
    queryFn: () => fetchNotifications({ cursor, tab, limit }),
    enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });
}

export function useNotificationsInfiniteQuery({
  tab = NotificationTabValue.ALL,
  limit = 20,
  enabled = true,
}: UseNotificationsQueryOptions = {}) {
  return useInfiniteQuery<NotificationListResponse, Error>({
    queryKey: notificationQueryKeys.infiniteList(tab, limit),
    queryFn: ({ pageParam }) =>
      fetchNotifications({ cursor: pageParam as string | null, tab, limit }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.paging.nextCursor ?? undefined,
    enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });
}

export function useNotificationUnreadCountQuery(enabled = true) {
  return useQuery<NotificationUnreadCountResponse, Error>({
    queryKey: notificationQueryKeys.unreadCount(),
    queryFn: fetchNotificationUnreadCount,
    enabled,
    staleTime: 30_000,
    placeholderData: (previousData) => previousData ?? EMPTY_UNREAD_COUNT_RESPONSE,
  });
}
